import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  SxProps,
  Theme,
} from '@mui/material';
import { Close, ArrowBack } from '@mui/icons-material';

export interface SideDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  onBack?: () => void;
  headerActions?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: number | string | Record<string, any>;
  maxWidth?: number | string | Record<string, any>;
  contentPadding?: number | string;
  bodySx?: SxProps<Theme>;
}

export const SideDrawer: React.FC<SideDrawerProps> = ({
  open,
  onClose,
  title,
  description,
  onBack,
  headerActions,
  children,
  footer,
  width,
  maxWidth,
  contentPadding,
  bodySx,
}) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      transitionDuration={{ enter: 300, exit: 250 }}
      SlideProps={{
        easing: {
          enter: 'cubic-bezier(0.22, 1, 0.36, 1)',
          exit: 'cubic-bezier(0.22, 1, 0.36, 1)',
        },
      }}
      ModalProps={{
        BackdropProps: {
          sx: {
            background: 'rgba(15, 23, 42, 0.12) !important',
            backdropFilter: 'blur(2px) !important',
            WebkitBackdropFilter: 'blur(2px) !important',
          },
        },
      }}
      PaperProps={{
        sx: {
          width: width || { xs: '100vw', sm: 540, md: 560 },
          maxWidth: maxWidth || '100vw',
          height: '100vh',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderLeft: '1px solid rgba(148, 163, 184, 0.18)',
          borderRadius: { xs: 0, sm: '24px 0 0 24px' },
          boxShadow: '-12px 0 40px rgba(15, 23, 42, 0.12)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          overflowX: 'hidden',
          outline: 'none',
          boxSizing: 'border-box',
        },
      }}
    >
      {/* ── Fixed Header ── */}
      {(title || description || onBack || onClose) && (
        <Box
          sx={{
            px: { xs: 2, sm: 2.75 },
            py: 2,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            borderBottom: '1px solid rgba(148, 163, 184, 0.16)',
            background: 'rgba(255, 255, 255, 0.88)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            flexShrink: 0,
            position: 'sticky',
            top: 0,
            zIndex: 2,
            overflowX: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25, width: '100%', minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1, overflow: 'hidden' }}>
              {onBack && (
                <IconButton
                  size="small"
                  onClick={onBack}
                  aria-label="go back"
                  sx={{
                    p: 0.75,
                    color: 'text.secondary',
                    borderRadius: '10px',
                    bgcolor: 'rgba(241, 245, 249, 0.7)',
                    border: '1px solid rgba(203, 213, 225, 0.6)',
                    flexShrink: 0,
                    '&:hover': { bgcolor: 'rgba(226, 232, 240, 0.9)' },
                  }}
                >
                  <ArrowBack fontSize="small" />
                </IconButton>
              )}
              <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                {typeof title === 'string' ? (
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      fontSize: '1.12rem',
                      color: '#0f172a',
                      letterSpacing: '-0.015em',
                      lineHeight: 1.3,
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                    }}
                  >
                    {title}
                  </Typography>
                ) : (
                  title
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
              {headerActions}
              <IconButton
                size="small"
                onClick={onClose}
                aria-label="close drawer"
                sx={{
                  color: '#64748b',
                  p: 0.75,
                  borderRadius: '10px',
                  bgcolor: 'rgba(241, 245, 249, 0.7)',
                  border: '1px solid rgba(203, 213, 225, 0.5)',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: 'rgba(241, 245, 249, 1)',
                    color: '#0f172a',
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {description && (
            <Box sx={{ mt: 0.25, pl: onBack ? 4.5 : 0, width: '100%', minWidth: 0 }}>
              {typeof description === 'string' ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: '#64748b',
                    fontSize: '0.84rem',
                    lineHeight: 1.4,
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }}
                >
                  {description}
                </Typography>
              ) : (
                description
              )}
            </Box>
          )}
        </Box>
      )}

      {/* ── Scrollable Body ── */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          p: contentPadding ?? { xs: 2, sm: 2.5 },
          display: 'flex',
          flexDirection: 'column',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(148, 163, 184, 0.3)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(148, 163, 184, 0.5)',
          },
          ...bodySx,
        }}
      >
        {children}
      </Box>

      {/* ── Sticky Footer Actions ── */}
      {footer && (
        <Box
          sx={{
            p: 2,
            px: { xs: 2, sm: 2.75 },
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            borderTop: '1px solid rgba(148, 163, 184, 0.16)',
            background: 'rgba(255, 255, 255, 0.92)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1.5,
            flexShrink: 0,
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            overflowX: 'hidden',
          }}
        >
          {footer}
        </Box>
      )}
    </Drawer>
  );
};

export default SideDrawer;
